// Artwork sizes: stored as the artist typed them, shown in the Site unit, plus a conversion for visitors on the other system.

import { SIZE_BRACKET_LIMITS, SIZE_BRACKETS } from '@/data/sizeBrackets'

const CM_PER = { cm: 1, mm: 0.1, inch: 2.54, ft: 30.48 }

// Site units are cm or inch; older configs may still say mm or ft
export function normalizeUnit(unit) {
  return { mm: 'cm', ft: 'inch' }[unit] || (unit === 'inch' ? 'inch' : 'cm')
}

// Inches to one decimal; cm whole, with one decimal under 10
function round(value, unit) {
  return unit === 'inch' || value < 10 ? Math.round(value * 10) / 10 : Math.round(value)
}

// One measurement in another unit; the stored number unchanged when the unit already matches
export function convertLength(value, from, to) {
  const number = parseFloat(value)
  if (!number || from === to) return value
  return round(number * (CM_PER[from] || 1) / CM_PER[to], to)
}

// { width, height } in the given unit
export function convertSize(size, unit) {
  return {
    width: convertLength(size?.width, size?.unit, unit),
    height: convertLength(size?.height, size?.unit, unit)
  }
}

// Inches for US browsers, cm for everyone else (including no region)
export function visitorUnit(language = typeof navigator !== 'undefined' ? navigator.language : '') {
  const region = (language || '').split('-')[1]?.toUpperCase()
  return region === 'US' ? 'inch' : 'cm'
}

// Stored unit 'inch' is written 'in', catalogue style
export const unitLabel = unit => (unit === 'inch' ? 'in' : unit)

// "110 × 222 cm" in the Site unit, with " (43.3 × 87.4 in)" added when the visitor uses the other unit
export function formatSize(size, siteUnit = 'cm', viewerUnit = visitorUnit()) {
  if (!size?.width || !size?.height) return ''
  const site = convertSize(size, siteUnit)
  const text = `${site.width} × ${site.height} ${unitLabel(siteUnit)}`
  if (viewerUnit === siteUnit) return text
  const visitor = convertSize(size, viewerUnit)
  return `${text} (${visitor.width} × ${visitor.height} ${unitLabel(viewerUnit)})`
}

// { width, height } in the given unit to two decimals (50.8 cm still counts as 20 in); null without a full size
function sizeForFilter(size, unit) {
  const width = parseFloat(size?.width)
  const height = parseFloat(size?.height)
  if (!width || !height) return null
  const to = value => Math.round(value * (CM_PER[size.unit] || 1) / CM_PER[unit] * 100) / 100
  return { width: to(width), height: to(height) }
}

// 'small' | 'medium' | 'large' by the longest side in the given unit; null without a full size
export function sizeBracket(size, unit) {
  const s = sizeForFilter(size, unit)
  if (!s) return null
  const longest = Math.max(s.width, s.height)
  const [small, medium] = SIZE_BRACKET_LIMITS[unit]
  if (longest < small) return 'small'
  return longest <= medium ? 'medium' : 'large'
}

// Filter options with their range: { value: 'small', text: 'small', range: 'under 50 cm' }
export function sizeBracketOptions(unit) {
  const [small, medium] = SIZE_BRACKET_LIMITS[unit]
  const ranges = [`under ${small}`, `${small} to ${medium}`, `over ${medium}`]
  return SIZE_BRACKETS.map((value, i) => ({ value, text: value, range: `${ranges[i]} ${unitLabel(unit)}` }))
}

// Visitor's custom size: optional from/to per side, in the visitor's unit
export const EMPTY_SIZE_RANGE = { minWidth: '', maxWidth: '', minHeight: '', maxHeight: '' }

const isBlank = value => value === '' || value == null

export function hasSizeRange(range) {
  return Object.values(range || {}).some(v => !isBlank(v))
}

// Empty boxes are ignored; works without a full size never match a range
export function matchesSizeRange(size, range, unit) {
  const s = sizeForFilter(size, unit)
  if (!s) return false
  const within = (value, min, max) =>
    (isBlank(min) || value >= Number(min)) && (isBlank(max) || value <= Number(max))
  return within(s.width, range.minWidth, range.maxWidth) && within(s.height, range.minHeight, range.maxHeight)
}
